import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  TextField,
  Grid,
  CircularProgress,
  createFilterOptions,
} from '@mui/material';
import { STATES_JSON } from '../../utils/locations';
import { UseFormRegister, UseFormSetValue } from 'react-hook-form';

const filterOptions = createFilterOptions<string>({ limit: 7 });
type State = { name: string; 'country_name': string };

export default function StateSelect({
  register,
  registerValue,
  setValue,
  selectedCountry,
  originalState,
  id,
}: {
  register: UseFormRegister<any>;
  registerValue: string;
  setValue: UseFormSetValue<any>;
  originalState?: string;
  id: string;
  selectedCountry?: string;
}) {
  const [options, setOptions] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [allStates, setAllStates] = useState<State[]>([]);

  useEffect(() => {
    setLoading(true);
    fetch(STATES_JSON)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load states (${res.status})`);
        return res.json() as Promise<State[]>;
      })
      .then((data) => {
        setAllStates(data);
        if (selectedCountry) {
          const filteredStates = data.reduce<string[]>((acc, state) => {
            if (state.country_name === selectedCountry) {
              acc.push(state.name);
            }
            return acc;
          }, []);
          setOptions(filteredStates);
        } else {
          setOptions(data.map((state) => state.name));
        }
      })
      .catch((err) => {
        console.error('Error loading states:', err);
        setOptions([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      const filteredStates = allStates.reduce<string[]>((acc, state) => {
        if (state.country_name === selectedCountry) {
          acc.push(state.name);
        }
        return acc;
      }, []);
      setOptions(filteredStates);
    } else {
      setOptions(allStates.map((state) => state.name));
    }

    // If originalState is provided, set it as the input value
    if (originalState) {
      setInputValue(originalState);
      setValue(registerValue, originalState);
    } else {
      setInputValue('');
    }
  }, [selectedCountry, allStates]);

  return (
    <Grid item xs={12} md={6}>
      <Autocomplete<string, false, false, true>
        freeSolo
        options={options}
        filterOptions={filterOptions}
        loading={loading}
        inputValue={inputValue}
        onInputChange={(_, newInput) => {
          setInputValue(newInput);
          setValue(registerValue, newInput);
        }}
        onChange={(_, newValue) => {
          const val = newValue ?? '';
          setInputValue(val);
          setValue(registerValue, val);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            {...register(registerValue)}
            fullWidth
            margin="normal"
            label="State"
            id={id}
            required
            autoComplete="off"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Grid>
  );
}
